import { useCallback, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { posService } from '@/services/pos.service';
import type { FulfillmentMode, PosComboSearchItem, PosVariantSearchItem } from '../types';

const PAGE_SIZE = 24;

/**
 * Khoá định danh ổn định cho từng loại item — dùng để chống nối trùng khi 2
 * trang khác nhau (hoặc 1 trang refetch nền) trả về cùng 1 sản phẩm/combo.
 *
 * Định nghĩa ở module scope (không phải trong render) để tham chiếu hàm luôn
 * ổn định giữa các lần render — tránh việc `useMemo` bên dưới tính lại mỗi
 * lần component cha re-render dù `query.data` không đổi.
 */
function getVariantKey(item: PosVariantSearchItem): string {
  return item.variant.variant_id;
}

function getComboKey(item: PosComboSearchItem): string {
  return item.combo_id;
}

/**
 * Phân trang tích luỹ cho lưới duyệt POS, dựng trên `useInfiniteQuery` của
 * TanStack Query (repo dùng v5.101.2 — xem `package.json`).
 *
 * LỊCH SỬ: bản đầu tự quản `page`/`accumulated` bằng `useState` + 2
 * `useEffect` riêng (một reset khi đổi `fulfillmentMode`, một tích luỹ
 * trang). Code review TCR-1126 phát hiện 2 lỗi vòng đời:
 *
 * 1. Đổi `fulfillmentMode` reset bằng `useEffect` chạy SAU render đầu tiên
 *    với mode mới → có ít nhất 1 khung hình `queryKey` đã đổi mode nhưng
 *    `page` (state) còn giá trị CŨ. Nếu cache của mode mới ở đúng `page` đó
 *    còn sống (`gcTime`), effect tích luỹ sẽ chèn nhầm dữ liệu trang cũ của
 *    mode mới vào vị trí "trang 1" trước khi effect reset kịp chạy.
 * 2. `loadMore` tự chặn gọi chồng bằng `query.isFetching` — một state bất
 *    đồng bộ, chỉ cập nhật sau re-render. Gọi `loadMore()` 2 lần trong cùng
 *    1 tick (2 sự kiện scroll sát nhau) lọt cả hai lần gọi, `page` nhảy từ 2
 *    lên 4 — bỏ qua hẳn trang 3, không có cơ chế nạp lại.
 *
 * `useInfiniteQuery` giải cả hai tận gốc thay vì tự vá: `fulfillmentMode` là
 * một phần của `queryKey` GỐC (không có page/pageParam trong key) nên đổi
 * mode khiến TanStack tự tạo query mới với `data.pages = []` — không còn
 * khung hình lệch của lỗi (1), không cần `useEffect` reset thủ công nữa.
 * `fetchNextPage` có guard nội bộ chống gọi chồng khi đang fetching — hết
 * lỗi (2), không cần tự viết cờ chặn.
 */
function usePagedBrowse<T>(
  key: string,
  fetchPage: (page: number) => Promise<{ items: T[]; has_more: boolean }>,
  getKey: (item: T) => string,
  fulfillmentMode: FulfillmentMode,
  enabled: boolean,
) {
  const query = useInfiniteQuery({
    queryKey: ['pos-browse', key, fulfillmentMode],
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage.has_more ? allPages.length + 1 : undefined),
    enabled,
    staleTime: 30_000,
    gcTime: 60_000,
    retry: false,
  });

  // Dedupe theo khoá định danh ổn định — GIỮ NGUYÊN quyết định đã chốt ở
  // vòng review trước (KHÔNG dùng JSON.stringify): field như `stock_quantity`
  // có thể đổi giá trị giữa 2 lần fetch (refetch nền sau `staleTime`), so
  // khớp theo toàn bộ nội dung item sẽ coi cùng 1 sản phẩm là "khác nhau" và
  // nối trùng dòng — vi phạm đúng yêu cầu "không nhân đôi hàng đã có".
  const items = useMemo(() => {
    if (!query.data) return [];
    const seen = new Set<string>();
    const result: T[] = [];
    for (const page of query.data.pages) {
      for (const item of page.items) {
        const itemKey = getKey(item);
        if (seen.has(itemKey)) continue;
        seen.add(itemKey);
        result.push(item);
      }
    }
    return result;
  }, [query.data, getKey]);

  const loadMore = useCallback(() => {
    if (!query.hasNextPage) return;
    void query.fetchNextPage();
  }, [query.hasNextPage, query.fetchNextPage]);

  return {
    items,
    hasMore: query.hasNextPage,
    // `isFetching && items.length === 0` thay vì field riêng theo version
    // (`isLoading` bị đổi ý nghĩa/tên ở v4 → v5 thành `isPending`, mà
    // `isPending` v5 còn true cả khi query bị `enabled: false` — không đúng
    // ý "đang tải") — cách này cho đúng nghĩa "đang tải và chưa có gì để
    // hiện" ở mọi version, giữ hành vi y hệt bản state/effect trước.
    isLoading: query.isFetching && items.length === 0,
    isError: query.isError,
    loadMore,
  };
}

export function usePosBrowseVariants(fulfillmentMode: FulfillmentMode, enabled: boolean) {
  return usePagedBrowse<PosVariantSearchItem>(
    'variants',
    (page) => posService.browseVariants(fulfillmentMode, page, PAGE_SIZE),
    getVariantKey,
    fulfillmentMode,
    enabled,
  );
}

export function usePosBrowseCombos(fulfillmentMode: FulfillmentMode, enabled: boolean) {
  return usePagedBrowse<PosComboSearchItem>(
    'combos',
    (page) => posService.browseCombos(fulfillmentMode, page, PAGE_SIZE),
    getComboKey,
    fulfillmentMode,
    enabled,
  );
}
